import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Download, Share } from "lucide-react";

const DISMISS_KEY = "cm_install_dismissed_at";
const DISMISS_DAYS = 7;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosMode, setIosMode] = useState(false);
  const [menuMode, setMenuMode] = useState(false);
  const [engaged, setEngaged] = useState(false);

  // Never cover the hero: appear only after the user scrolls past it
  // (or after 45s of dwell time as a fallback).
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 550) setEngaged(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(() => setEngaged(true), 45000);
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, []);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissedAt < DISMISS_DAYS * 24 * 3600 * 1000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    let fallbackTimer;
    if (isIOS()) {
      fallbackTimer = setTimeout(() => {
        setIosMode(true);
        setMenuMode(true);
        setShow(true);
      }, 2500);
    } else {
      // Android/other: always invite after a short delay, even if the
      // native install event never fires (in-app browsers, etc.)
      fallbackTimer = setTimeout(() => setShow(true), 3000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (iosMode || !deferred) { setMenuMode(true); return; }
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setShow(false);
      else dismiss();
      setDeferred(null);
    } catch {
      setDeferred(null);
      setMenuMode(true);
    }
  };

  if (!show || !engaged) return null;
  if (typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/course/") || window.location.pathname.startsWith("/free-quiz"))) return null;

  return (
    <div
      className="fixed bottom-20 md:bottom-4 inset-x-4 mx-auto z-[70] max-w-md rounded-2xl border border-border bg-background shadow-2xl p-4 animate-fade-in"
      data-testid="install-prompt-banner"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        data-testid="install-prompt-dismiss-btn"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="ClassMate" className="w-12 h-12 rounded-xl shadow" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Install ClassMate</p>
          {iosMode ? (
            menuMode ? (
              <ol className="text-xs text-muted-foreground list-decimal ms-4 space-y-0.5" data-testid="install-prompt-ios-steps">
                <li>Tap the <Share className="h-3.5 w-3.5 inline text-blue-500" /> Share button (bottom of Safari)</li>
                <li>Scroll and tap <b>"Add to Home Screen"</b></li>
                <li>Tap <b>Add</b> — done!</li>
              </ol>
            ) : (
              <p className="text-xs text-muted-foreground">Get the full app experience — fast access from your home screen.</p>
            )
          ) : menuMode ? (
            <p className="text-xs text-muted-foreground" data-testid="install-prompt-menu-hint">
              Open your browser menu (⋮) and tap "Add to Home screen" / "Install app"
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Get the full app experience — fast access from your home screen.</p>
          )}
        </div>
        {menuMode && iosMode && typeof navigator !== "undefined" && navigator.share ? (
          <Button size="sm" onClick={() => navigator.share({ title: "ClassMate", url: window.location.origin }).catch(() => {})} className="shrink-0" data-testid="install-prompt-share-btn">
            <Share className="h-4 w-4 mr-1" /> Open Share
          </Button>
        ) : !menuMode && (
          <Button size="sm" onClick={install} className="shrink-0" data-testid="install-prompt-install-btn">
            <Download className="h-4 w-4 mr-1" /> Install
          </Button>
        )}
      </div>
    </div>
  );
};
